import { defineStore } from 'pinia';
import { hasTier, planTier } from '@/config/plans';

export const useUserStore = defineStore('user', {

    state: () => ({
        user: null,
        company: null,
        isLoading: false,
        childCompanies: [],
        selectedCompanyId: null,
    }),

    getters: {
        // ── Subscription / plan gating ──────────────────────────────────
        // Source of truth: company.subscriptionType ("baseline" | "launchpad" |
        // "cohort" | "cohort_premium" | "maintenance" | null). null → baseline
        // (el piso). El acceso "cohort" es cohort o superior (hasTier), así que
        // cohort_premium y maintenance se comportan igual que cohort.
        subscriptionType: (state) => state.company?.subscriptionType ?? null,

        // El escalón, por nombre. Con tres tiers "¿soy cohort o no?" dejó de
        // alcanzar: hay preguntas que necesitan saber CUÁL es el plan.
        tier: (state) => planTier(state.company?.subscriptionType),

        isCohort:    (state) => hasTier('cohort', state.company?.subscriptionType),
        isBaseline:  (state) => planTier(state.company?.subscriptionType) === 'baseline',

        // ── Vigencia de la suscripción (docs/subscription-expiry-fe-contract.md) ──
        subscription:                (state) => state.company?.subscription ?? null,
        subscriptionStatus:          (state) => state.company?.subscription?.status ?? 'none',
        subscriptionDaysLeft:        (state) => state.company?.subscription?.daysLeft ?? null,
        subscriptionCurrentPeriodEnd:(state) => state.company?.subscription?.currentPeriodEnd ?? null,
        subscriptionCancelAtPeriodEnd:(state) => state.company?.subscription?.cancelAtPeriodEnd ?? false,
        isSubscriptionInGrace:       (state) => state.company?.subscription?.status === 'grace',
        isSubscriptionLapsed:        (state) => state.company?.subscription?.status === 'lapsed',

        // ⚠️ `isLaunchpad` NO es "está en launchpad": es "no llega a cohort", y
        // con baseline abajo eso ahora incluye DOS planes. Se mantiene el nombre
        // y la semántica porque así lo usan sus 14 consumidores —todos preguntan
        // "¿le falta plan para esto?", no "¿qué plan tiene?"— y cambiarla
        // silenciosamente le daría a baseline la UI completa de launchpad.
        // Para "está exactamente en launchpad", usar `tier === 'launchpad'`.
        isLaunchpad: (state) => !hasTier('cohort', state.company?.subscriptionType),

        // Feature gates — derived from plan (cohort o superior)
        canAccessMonitor:        (state) => hasTier('cohort', state.company?.subscriptionType),
        canAccessNetwork:        (state) => hasTier('cohort', state.company?.subscriptionType),
        canAccessFullActionPlan: (state) => hasTier('cohort', state.company?.subscriptionType),

        // El plan de acción reducido ("Fundamentals", /sustainable) y todo lo
        // que hoy ve launchpad: baseline NO llega. Es el gate que separa los
        // dos tiers de abajo, y el que no existía antes de baseline.
        canAccessActionPlan:     (state) => hasTier('launchpad', state.company?.subscriptionType),
    },

    actions: {
        handleLoading(flag){
            this.isLoading = flag;
        },
        loadUser(user) {
            this.user = user
        },
        loadCompany(company) {
            this.company = company
        },
        // Patchea solo `subscription`, sin pisar el resto de `company`.
        updateSubscription(subscription) {
            this.company = { ...this.company, subscription };
        },
        loadChildCompanies(companies) {
            this.childCompanies = companies;
        },
        setSelectedCompany(id) {
            this.selectedCompanyId = id;
        },
        clearSelectedCompany() {
            this.selectedCompanyId = null;
        },
        clearState() {
            this.user  = null;
            this.company = null;
            this.isLoading = false;
            this.childCompanies = [];
            this.selectedCompanyId = null;
        }
    }
});

